import type { HazardIdentification } from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  ChevronsRight,
  MoreHorizontal,
  RefreshCcw,
  MapPinIcon,
  ShieldCheckIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'
import { getIso7010PictogramUrl } from './iso7010Pictograms'
import { HazardRiskScore } from './HazardRiskScore'
import { RiskInFactorsGrid } from './RiskInFactorsGrid'
import { Badge } from '@/components/ui/badge'
import { getHazardStandardProvenanceLabel } from './getHazardStandardProvenanceLabel'
import { isHazardUncertainReview } from './hazardUncertainReview'

function getHazardTypeLabel(
  type: string | null,
  naLabel: string,
  translate: (key: string) => string
) {
  if (!type) {
    return naLabel
  }
  return translate(`hazardsPage.enums.hazardTypes.${type}`) || type
}

export function HazardListCard({
  hazard,
  locationLabel,
  onEdit,
  onDelete,
  onKeep,
  isMutationPending,
}: {
  hazard: HazardIdentification
  locationLabel: string | null
  onEdit: (hazard: HazardIdentification) => void
  onDelete: (hazardId: string) => void
  onKeep: (hazardId: string) => void
  isMutationPending: boolean
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const naLabel = t('hazardsPage.hazardDetail.na')
  const displayText =
    hazard.description?.trim() ||
    hazard.type ||
    locationLabel ||
    t('hazardsPage.noDescription')
  const standardProvenanceLabel = getHazardStandardProvenanceLabel(hazard, t)
  const needsReview = isHazardUncertainReview(hazard.uncertain)

  return (
    <Card>
      <CardHeader>
        <div className='flex flex-row items-center justify-between'>
          <CardTitle className='flex flex-wrap items-center gap-2'>
            {hazard.pictogramId != null && hazard.pictogramId.trim() !== '' ? (
              <img
                src={getIso7010PictogramUrl(hazard.pictogramId)}
                alt=""
                className='size-8 shrink-0 object-contain'
              />
            ) : null}
            {getHazardTypeLabel(hazard.type, naLabel, t)} (#{hazard.id})
            {needsReview && (
              <Badge variant='warning'>{t('hazardsPage.needsReviewBadge')}</Badge>
            )}
            {standardProvenanceLabel != null && (
              <Badge
                variant='outline'
                className='font-normal text-muted-foreground'
              >
                {standardProvenanceLabel}
              </Badge>
            )}
            {hazard.riskAdequatelyReducedAt != null && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className='inline-flex' role='img'>
                    <ShieldCheckIcon
                      className='size-5 shrink-0 text-green-600'
                      aria-hidden
                    />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  {t('hazardsPage.hazardDetail.riskFlow.riskAdequatelyReduced')}
                </TooltipContent>
              </Tooltip>
            )}
          </CardTitle>
          <div className='flex shrink-0 items-center gap-2'>
            {needsReview && (
              <>
                <Button
                  size='sm'
                  onClick={() => onKeep(hazard.id)}
                  disabled={isMutationPending}
                >
                  {t('hazardsPage.keepUncertain')}
                </Button>
                <Button
                  variant='outline'
                  size='sm'
                  onClick={() => onDelete(hazard.id)}
                  disabled={isMutationPending}
                >
                  {t('hazardsPage.removeUncertain')}
                </Button>
              </>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant='ghost'
                  size='icon'
                  className='size-8'
                  disabled={isMutationPending}
                >
                  <MoreHorizontal className='size-4' />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align='end'>
                <DropdownMenuItem asChild>
                  <Link to={hazard.id}>{t('hazardsPage.view')}</Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onEdit(hazard)}>
                  {t('hazardsPage.hazardDetail.edit')}
                </DropdownMenuItem>
                {!needsReview && (
                  <DropdownMenuItem
                    onClick={() => onDelete(hazard.id)}
                    className='text-destructive focus:text-destructive'
                  >
                    {t('hazardsPage.hazardDetail.delete')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardHeader>
      <CardContent className='space-y-2 pt-4'>
        <HoverCard>
          <HoverCardTrigger asChild>
            <p
              className='line-clamp-2 cursor-default text-sm text-muted-foreground'
              title={displayText}
            >
              {displayText}
            </p>
          </HoverCardTrigger>
          <HoverCardContent className='max-w-sm' align='start'>
            <p className='text-sm'>{displayText}</p>
          </HoverCardContent>
        </HoverCard>
        <div className='flex items-center gap-2'>
          <HoverCard>
            <HoverCardTrigger asChild>
              <button
                type='button'
                className='inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                aria-label={t('hazardsPage.locationLabel')}
              >
                <MapPinIcon className='size-4' />
              </button>
            </HoverCardTrigger>
            <HoverCardContent align='start'>
              <p className='text-sm font-medium'>
                {t('hazardsPage.locationLabel')}
              </p>
              <p className='mt-1 text-sm text-muted-foreground'>
                {locationLabel ?? '—'}
              </p>
            </HoverCardContent>
          </HoverCard>
          <HoverCard>
            <HoverCardTrigger asChild>
              <button
                type='button'
                className='inline-flex size-8 shrink-0 items-center justify-center rounded-md border border-input bg-background text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                aria-label={t('hazardsPage.lifecyclesLabel')}
              >
                <RefreshCcw className='size-4' />
              </button>
            </HoverCardTrigger>
            <HoverCardContent align='start'>
              <p className='text-sm font-medium'>
                {t('hazardsPage.lifecyclesLabel')}
              </p>
              <p className='mt-1 text-sm text-muted-foreground'>
                {hazard.lifecycles.length > 0
                  ? hazard.lifecycles
                      .map((lifecycle) =>
                        t(`hazardsPage.enums.hazardLifecycles.${lifecycle}`)
                      )
                      .join(', ')
                  : '—'}
              </p>
            </HoverCardContent>
          </HoverCard>
        </div>
        <div className='grid grid-cols-1 gap-2'>
          <RiskInFactorsGrid
            layout='fourColumn'
            injury={hazard.riskInInjury}
            frequency={hazard.riskInFrequency}
            likelihood={hazard.riskInLikelihood}
            avoidance={hazard.riskInAvoidance}
          />

          <Item variant='outline'>
            <ItemContent>
              <ItemTitle></ItemTitle>
              <ItemDescription>
                <div className='flex items-center justify-center gap-4 text-lg'>
                  <div>
                    <HazardRiskScore
                      variant='compact'
                      score={hazard.riskInScore}
                    />
                    <span className='text-sm text-muted-foreground'>
                      {t('hazardsPage.hazardDetail.riskInScore')}
                    </span>
                  </div>
                  <>
                    <div className='flex grow-0 items-center gap-2'>
                      <ChevronsRight className='size-4' />
                      <Link
                        to={hazard.id}
                        className='text-sm break-all text-muted-foreground'
                      >
                        <Button variant='link' size='sm'>
                          {' '}
                          {t('hazardsPage.riskAssessmentMeasuresCount', {
                            count: hazard.hazardRiskReductionStepsCount ?? 0,
                          })}{' '}
                        </Button>
                      </Link>
                      <ChevronsRight className='size-4' />
                    </div>

                    <div>
                      <HazardRiskScore
                        variant='compact'
                        score={
                          hazard.finalRiskOutScore ?? hazard.riskInScore
                        }
                      />
                      <span className='text-sm text-muted-foreground'>
                        {t('hazardsPage.finalRiskOutScoreLabel')}{' '}
                      </span>
                    </div>
                  </>
                </div>
              </ItemDescription>
            </ItemContent>
          </Item>
        </div>
      </CardContent>
    </Card>
  )
}
